import React, { useEffect, useRef } from 'react';
import { TouchableOpacity, ActivityIndicator } from 'react-native';
import styled from '@emotion/native';
import { BodyText } from '@/shared/components/StyledText';
import { green } from '@/shared/styles/colors';
import { ReceiptIcon, ReceiptValue } from './ReceiptItem';
import { Consultation, ConsultationStatus } from '@/shared/types/generated';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useStripe } from '@/services/stripe/StripeProvider';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_DEFAULT_PAYMENT_METHOD } from '@/shared/queries';
import { UPDATE_CONSULTATION_PAYMENT_METHOD } from '@/shared/mutations';
import { QueryResult } from '@/shared/types/generated';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';

interface PaymentMethodDisplayProps {
  consultation?: Consultation;

  // Callbacks
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onPaymentMethodStateChange?: (hasPaymentMethod: boolean) => void;
}

const PaymentMethodDisplay: React.FC<PaymentMethodDisplayProps> = ({
  consultation,
  onSuccess,
  onError,
  onPaymentMethodStateChange,
}) => {
  const { user, isLoadingUser } = useGetUser();
  const { openPaymentMethodSheet } = useStripe();
  const { setSnackbarState } = useStore();
  const previousPaymentMethodState = useRef<boolean | null>(null);

  const [
    getDefaultPaymentMethod,
    { data: defaultPaymentMethod, loading: isLoadingDefaultPaymentMethod },
  ] = useLazyQuery<QueryResult<'getDefaultPaymentMethod'>>(
    GET_DEFAULT_PAYMENT_METHOD,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [updateConsultationPaymentMethod, { loading: isUpdating }] =
    useMutation(UPDATE_CONSULTATION_PAYMENT_METHOD, {
      onCompleted: () => {
        setSnackbarState({
          open: true,
          message: 'Payment method updated successfully!',
          severity: 'success',
        });
        onSuccess?.();
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to update payment method';
        setSnackbarState({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        onError?.(errorMessage);
      },
    });

  // Check if payment method changes should be disabled
  const isPaymentMethodChangeDisabled =
    consultation &&
    [
      ConsultationStatus.Completed,
      ConsultationStatus.Cancelled,
      ConsultationStatus.InProgress,
      ConsultationStatus.ClientNoShow,
      ConsultationStatus.StylistNoShow,
    ].includes(consultation.status);

  const handleEditPress = async () => {
    if (isPaymentMethodChangeDisabled) {
      return;
    }

    if (!user?.client?.stripeCustomerId) {
      const errorMessage = 'No Stripe customer ID found';
      SentryService.captureError(
        `Client ${user?.client?.id} does not have a Stripe customer id`,
      );
      onError?.(errorMessage);
      return;
    }

    try {
      await openPaymentMethodSheet(user.client.stripeCustomerId);
      await getDefaultPaymentMethod();

      // If this is for a consultation, update the consultation payment method
      if (consultation?.id) {
        await updateConsultationPaymentMethod({
          variables: {
            input: {
              consultationId: consultation.id,
            },
          },
        });
      } else {
        onSuccess?.();
      }
    } catch (error) {
      SentryService.captureError('Error updating payment method:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update payment method';
      onError?.(errorMessage);
    }
  };

  useEffect(() => {
    // Load default payment method when component mounts if user has one
    if (user?.client?.stripeDefaultPaymentMethodId) {
      getDefaultPaymentMethod();
    }
  }, [user?.client?.stripeDefaultPaymentMethodId, getDefaultPaymentMethod]);

  useEffect(() => {
    if (!isLoadingDefaultPaymentMethod && !isLoadingUser) {
      const paymentMethodLast4 =
        defaultPaymentMethod?.getDefaultPaymentMethod?.last4;
      const hasPaymentMethod = !!paymentMethodLast4;

      // Only call the callback if the state actually changed
      if (previousPaymentMethodState.current !== hasPaymentMethod) {
        previousPaymentMethodState.current = hasPaymentMethod;
        onPaymentMethodStateChange?.(hasPaymentMethod);
      }
    }
  }, [
    isLoadingDefaultPaymentMethod,
    isLoadingUser,
    defaultPaymentMethod?.getDefaultPaymentMethod?.last4,
    onPaymentMethodStateChange,
  ]);

  if (isLoadingUser || isLoadingDefaultPaymentMethod || isUpdating) {
    return <ActivityIndicator size="small" color={green} />;
  }

  const paymentMethodLast4 =
    defaultPaymentMethod?.getDefaultPaymentMethod?.last4;
  const hasPaymentMethod = !!paymentMethodLast4;

  // If no payment method exists, show "Add Payment" option
  if (!hasPaymentMethod) {
    return (
      <TouchableOpacity
        onPress={handleEditPress}
        disabled={isPaymentMethodChangeDisabled}
      >
        <LinkText disabled={isPaymentMethodChangeDisabled}>
          Add Payment
        </LinkText>
      </TouchableOpacity>
    );
  }

  // If payment method exists, show "Change" option with card details
  return (
    <PaymentMethodContainer>
      {!isPaymentMethodChangeDisabled && (
        <TouchableOpacity onPress={handleEditPress}>
          <LinkText>Change</LinkText>
        </TouchableOpacity>
      )}
      <ReceiptIcon source={require('@/assets/images/Card.png')} />
      <ReceiptValue>{paymentMethodLast4}</ReceiptValue>
    </PaymentMethodContainer>
  );
};

const PaymentMethodContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
});

const LinkText = styled(BodyText)<{ disabled?: boolean }>(({ disabled }) => ({
  color: disabled ? '#999' : green,
  fontWeight: 500,
  textDecorationLine: disabled ? 'none' : 'underline',
  fontSize: 14,
  marginRight: 10,
}));

export default PaymentMethodDisplay;
